import type { Norma } from '../api/types';

const DB_NAME = 'lexcore_cache_db';
const STORE_NAME = 'cached_norms';
const DB_VERSION = 1;
const MAX_CACHED_NORMS = 50;

function getDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getLocalNorma(source: string, id: string): Promise<Norma | null> {
  try {
    const db = await getDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const key = `${source}:${id}`;
      const request = store.get(key);
      request.onsuccess = () => {
        const record = request.result;
        resolve(record ? record.data : null);
      };
      request.onerror = () => resolve(null);
    });
  } catch (e) {
    console.error('Failed to get cached norm from IndexedDB:', e);
    return null;
  }
}

export async function saveLocalNorma(source: string, id: string, data: Norma): Promise<void> {
  try {
    const db = await getDb();
    
    // Guardamos la norma
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const key = `${source}:${id}`;
      const request = store.put({
        cacheKey: key,
        data,
        timestamp: Date.now()
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    // Mantener límite de 50 normas en caché (evitar desborde)
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const records = request.result || [];
      if (records.length > MAX_CACHED_NORMS) {
        // Ordenar por timestamp viejo primero
        records.sort((a, b) => a.timestamp - b.timestamp);
        const toDelete = records.length - MAX_CACHED_NORMS;
        for (let i = 0; i < toDelete; i++) {
          store.delete(records[i].cacheKey);
        }
      }
    };
  } catch (e) {
    console.error('Failed to save cached norm to IndexedDB:', e);
  }
}
